// Folgen (Entscheidung 38, im Lernablauf seit Entscheidung 39, docs/FOLGEN_PILOT.md): kurze Dialoge mit fester Besetzung, freigeschaltet nach
// einer Lektion. Reine Logik ohne Oberfläche. Der Zustand steht im Protokoll C1 (Ereignisart exposure, Rolle collection, Ziel = Kennung der Folge)
// und wandert damit wie alle Lernereignisse über die Synchronisierung auf andere Geräte. Folgen ändern keine Fälligkeit und zählen nicht als
// Hörnachweis: deriveC1 übergeht exposure (learning-policy.js, attemptsOf).
import { stableUuid } from './ids.js';
import { studyDay, SCHEDULER_VERSION } from './learning-policy.js';

export const RELISTEN_DAYS = [1, 7, 30];   // Wiederhören ohne Text: 1 Tag, 1 Woche, 1 Monat nach dem letzten Hören (Arne 25.10.2026)
export const CARD_AFTER = 8;               // Folgen-Karte nach 8 erledigten Aufgaben (Teil 2: seit Teil 1, sonst am Lerntag) oder am Ende der Sitzung
// Schritte: opened (geöffnet), pass1 (mit Text zu Ende gehört), translation (Übersetzung gezeigt), answer (Verständnisfrage), pass2 (ohne Text
// zu Ende gehört), relisten (später ohne Text wiederholt), later („Später“), skipped („Überspringen“: kommt nicht mehr als Karte, im Reiter weiter offen)
export const STEPS = ['opened', 'pass1', 'translation', 'answer', 'pass2', 'relisten', 'later', 'skipped'];

// Ein Ereignis für learning_events_v2. ctx wie core.c1Context(sessionId); extra: { q, choice, correct, activeMs, meta }.
// Feste ID je Durchgang, Schritt und Frage: zweimal speichern ergibt ein Ereignis; „Später“ einmal je Folge und Lerntag.
export function exposure(ctx, folgeId, attemptId, step, at, extra = {}) {
  if (!STEPS.includes(step)) throw new Error('unbekannter Schritt ' + step);
  const day = studyDay(at, ctx.tz);
  const att = step === 'later' ? `later|${folgeId}|${day}` : attemptId;
  const meta = { folge_step: step };
  if (extra.q !== undefined) Object.assign(meta, { q: extra.q, choice: extra.choice, correct: !!extra.correct });
  if (extra.meta) Object.assign(meta, extra.meta);   // z. B. { via, heard, lines, translation, listen_first } beim Abschluss eines Durchgangs
  return {
    id: stableUuid(`${att}|exposure|${step}|${extra.q ?? ''}`), deck: ctx.deck, target_id: folgeId, target_revision: 1, sentence_id: extra.line || null,
    sentence_revision: null, context_group_id: null, content_version: ctx.contentVersion || null, event_kind: 'exposure', attempt_id: att,
    session_id: ctx.sessionId || null, skill: null, task_role: 'collection', occurred_at: at, study_day: day, learning_timezone: ctx.tz,
    policy_version: ctx.policy, scheduler_version: SCHEDULER_VERSION, active_ms: extra.activeMs ?? null, meta,
  };
}

// Zustand je Folge aus den Ereignissen: früheste Zeit je Schritt, alle Wiederhör-Zeiten, erste Antwort je Frage, Lerntage mit „Später“.
export function stateOf(events, folgen) {
  const st = Object.fromEntries(folgen.map(f => [f.id, { opened: null, pass1: null, pass2: null, skipped: null, relistens: [], answers: {}, later: new Set() }]));
  const xs = events.filter(e => e.event_kind === 'exposure' && st[e.target_id]).sort((a, b) => a.occurred_at - b.occurred_at);
  for (const e of xs) {
    const s = st[e.target_id], step = e.meta && e.meta.folge_step;
    if (step === 'answer') { if (!(e.meta.q in s.answers)) s.answers[e.meta.q] = !!e.meta.correct; }
    else if (step === 'later') s.later.add(e.study_day);
    else if (step === 'relisten') s.relistens.push(e.occurred_at);
    else if (step in s && s[step] === null) s[step] = e.occurred_at;
  }
  return st;
}

// known(u): Zeitpunkt, ab dem das Lernziel als bekannt gilt (kennengelernt; per Einstufung vorausgesetzt oder ausgeblendet: 0), sonst null.
// true/false gehen auch (true = seit jeher bekannt).
const isKnown = v => v !== null && v !== undefined && v !== false;
// Letzte Position der Lektion L (aus content.blocks „Lektion L“), sonst null (Lektion nicht in diesem Inhalt).
// Lücken in der Nummerierung (IT ohne Lektion 30 und 66, FR ohne 93): Fehlt L, gibt es aber eine spätere Lektion, gilt das Ende der letzten
// Lektion davor; sonst bliebe eine Folge „nach Lektion 93“ für immer gesperrt. Hinter der letzten Lektion bleibt es null.
export function lessonEnd(content, L) {
  const nr = b => Number((/^Lektion (\d+)$/.exec(b.title) || [])[1]), blocks = (content.blocks || []).filter(b => nr(b) > 0);
  const b = blocks.find(x => nr(x) === L);
  if (b) return b.to;
  if (!blocks.some(x => nr(x) > L)) return null;
  const before = blocks.filter(x => nr(x) < L);
  return before.length ? Math.max(...before.map(x => x.to)) : 0;
}
// Zeitpunkt, zu dem alle Lernziele bis einschließlich Lektion L bekannt waren, sonst null. Lernziele ohne C1-Aufgaben zählen nicht, sie kommen
// im Kartenablauf C1 nie dran.
export function lessonDoneAt(content, L, known) {
  const to = lessonEnd(content, L);
  if (to === null) return null;
  let at = 0;
  for (const u of content.units) {
    if (u.pos > to || !u.c1) continue;
    const k = known(u);
    if (!isKnown(k)) return null;
    if (typeof k === 'number' && k > at) at = k;
  }
  return at;
}
export const lessonDone = (content, L, known) => lessonDoneAt(content, L, known) !== null;
export const isUnlocked = (f, content, known) => !!f.vorschau || lessonDone(content, f.nach, known);
// Vorschau-Modus: Folge ist offen, aber ihre Lektion noch nicht geschafft (Übersetzung als Hilfe von Anfang an sichtbar)
export const isPreview = (f, content, known) => !!f.vorschau && !lessonDone(content, f.nach, known);

// Lerntage als fortlaufende Zahl (studyDay liefert JJJJ-MM-TT), damit „1 Tag, 1 Woche, 1 Monat“ nach Kalendertagen zählt
const dayNo = d => Math.round(Date.parse(d + 'T00:00:00Z') / 864e5);
const dayStr = n => new Date(n * 864e5).toISOString().slice(0, 10);
// Lerntag, ab dem das nächste Wiederhören fällig ist (frühestens; die Vorschau-Folge zusätzlich erst nach ihrer Lektion), sonst null:
// noch nicht beide Durchgänge gehört oder alle drei Wiederhör-Termine erledigt.
export function relistenFrom(s, dayOf) {
  if (!s || s.skipped || s.pass1 === null || s.pass2 === null) return null;
  const k = s.relistens.length;
  if (k >= RELISTEN_DAYS.length) return null;
  const last = Math.max(s.pass2, ...s.relistens);
  return dayStr(dayNo(dayOf(last)) + RELISTEN_DAYS[k]);
}
export const relistenDue = (f, content, s, known, today, dayOf) => {
  const from = relistenFrom(s, dayOf);
  return from !== null && today >= from && lessonDone(content, f.nach, known);
};

// Folgen-Karte im Kartenablauf (Arne 25.10.2026): höchstens eine Folge je Lerntag, neue vor Wiederhören; keine an Tagen „ohne Ton“ und keine
// nach „Später“ an diesem Lerntag. Als Karte kommt eine Folge erst nach ihrer Lektion (die Vorschau-Folge ist vorher nur im Reiter offen).
//   neu:    als nächste Karte, wenn die Lektion heute geschafft wurde; sonst nach CARD_AFTER Aufgaben des Tages oder am Ende der Sitzung.
//   teil2:  ohne Text noch einmal hören, CARD_AFTER Aufgaben nach Teil 1 oder am Ende der Sitzung (Teil 1 an einem früheren Tag: wie wieder).
//   wieder: Wiederhören nach RELISTEN_DAYS, nach CARD_AFTER Aufgaben des Tages oder am Ende der Sitzung.
// o: { today, dayOf, noAudio, queueEmpty (sonst nichts mehr zu tun), doneToday (erledigte Aufgaben heute), doneSince(t) (erledigt nach t) }
export function cardFor(folgen, content, st, known, o) {
  const { today, dayOf, noAudio = false, queueEmpty = false, doneToday = 0, doneSince = () => 0 } = o;
  if (noAudio) return null;
  const all = Object.values(st);
  if (all.some(s => s.later.has(today))) return null;
  const late = queueEmpty || doneToday >= CARD_AFTER;
  for (const f of folgen) {
    const s = st[f.id];
    if (s.skipped || s.pass1 === null || s.pass2 !== null || !lessonDone(content, f.nach, known)) continue;
    if (dayOf(s.pass1) === today ? queueEmpty || doneSince(s.pass1) >= CARD_AFTER : late) return { folge: f, mode: 'teil2' };
  }
  if (all.some(s => [s.pass1, s.skipped, ...s.relistens].some(t => t && dayOf(t) === today))) return null;   // auch eine übersprungene zählt als die Folge des Tages
  for (const f of folgen) {
    if (st[f.id].pass1 !== null || st[f.id].skipped) continue;
    const at = lessonDoneAt(content, f.nach, known);
    if (at === null) continue;
    return at > 0 && dayOf(at) === today || late ? { folge: f, mode: 'neu' } : null;   // neue zuerst: kein Wiederhören, solange eine neue wartet
  }
  const again = folgen.find(f => relistenDue(f, content, st[f.id], known, today, dayOf));
  return again && late ? { folge: again, mode: 'wieder' } : null;
}

// Erledigte Aufgaben im Kartenablauf C1 (kennengelernt oder bewertet), für cardFor: an einem Lerntag oder nach einem Zeitpunkt
export const doneTasks = (attempts, { day = null, after = null } = {}) => attempts.filter(a => (a.introducedAt !== null || a.review)
  && (day === null || (a.events[0] && a.events[0].study_day) === day) && (after === null || (a.presentedAt ?? 0) > after)).length;

// Modus beim Öffnen aus dem Reiter: neu (noch nie mit Text zu Ende), teil2 (noch nicht ohne Text), wieder (Wiederhören fällig), sonst frei (nur
// anhören, nichts festhalten).
export function modeFor(f, content, st, known, today, dayOf) {
  const s = st[f.id];
  if (!s || s.pass1 === null) return 'neu';
  if (s.pass2 === null) return 'teil2';
  if (relistenDue(f, content, s, known, today, dayOf)) return 'wieder';
  return 'frei';
}
